import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loading } from '@/components/Loading';
import { toast } from 'sonner';
import { profileDocumentsApi } from '@/lib/api/profileDocuments';
import { caseStudiesApi } from '@/lib/api/case-studies';
import {
  credentialItemStatusKey,
  credentialStatusBadgeVariant,
} from '@/lib/credentialItemStatus';
import {
  CredentialReviewStatus,
  ProfileDocumentKind,
  ProfileDocumentKindValues,
  type CaseStudyDraftFromFileDto,
  type ProfileDocumentDto,
  type ProfileDocumentKind as ProfileDocumentKindType,
} from '@paladin-monorepo/common';
import { Download, GripVertical, Replace, Trash2, Upload, X } from 'lucide-react';
import {
  reportProfileDocumentUploadError,
  resolveProfileDocumentUploadError,
  validateProfileDocumentFile,
} from '@/lib/profileDocumentUpload';

export interface ProfileCredentialsCardProps {
  onCaseStudyDraft?: (draft: CaseStudyDraftFromFileDto, file: File) => void;
}

export function ProfileCredentialsCard({
  onCaseStudyDraft,
}: ProfileCredentialsCardProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<ProfileDocumentKindType>('cv');
  const [file, setFile] = useState<File | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['profile-documents'],
    queryFn: () => profileDocumentsApi.list(),
  });

  const replacementsByOriginal = useMemo(() => {
    const map = new Map<string, ProfileDocumentDto>();
    for (const doc of docs) {
      if (doc.replacesDocumentId) {
        map.set(doc.replacesDocumentId, doc);
      }
    }
    return map;
  }, [docs]);

  const canonicalDocs = useMemo(
    () => docs.filter((doc) => !doc.replacesDocumentId),
    [docs],
  );

  const invalidateDocs = () => {
    qc.invalidateQueries({ queryKey: ['profile-documents'] });
    qc.invalidateQueries({ queryKey: ['profile'] });
  };

  const uploadMut = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new Error('no file');
      }
      const validation = validateProfileDocumentFile(file);
      if (!validation.ok) {
        throw Object.assign(new Error(validation.messageKey), {
          clientValidationKey: validation.messageKey,
        });
      }
      return profileDocumentsApi.upload(file, kind);
    },
    onSuccess: () => {
      invalidateDocs();
      setFile(null);
      toast.success(t('profile.credentials.uploadSuccess'));
    },
    onError: (error: unknown) => {
      const clientKey =
        error &&
        typeof error === 'object' &&
        'clientValidationKey' in error &&
        typeof (error as { clientValidationKey?: unknown }).clientValidationKey === 'string'
          ? (error as { clientValidationKey: string }).clientValidationKey
          : null;

      if (clientKey) {
        toast.error(t(clientKey as 'profile.credentials.fileTooLarge'));
        return;
      }

      const resolved = resolveProfileDocumentUploadError(error);
      if (resolved.reportToSentry) {
        reportProfileDocumentUploadError(error, {
          kind,
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
        });
      }
      toast.error(t(resolved.messageKey));
    },
  });

  const draftMut = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new Error('no file');
      }
      return caseStudiesApi.draftFromFile(file);
    },
    onSuccess: (draft) => {
      if (!file) return;
      onCaseStudyDraft?.(draft, file);
      setFile(null);
      toast.success(t('profile.credentials.caseStudyDraftSuccess'));
    },
    onError: () => {
      toast.error(t('profile.credentials.caseStudyDraftError'));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => profileDocumentsApi.remove(id),
    onSuccess: () => {
      invalidateDocs();
      toast.success(t('profile.credentials.deleteSuccess'));
    },
    onError: () => {
      toast.error(t('profile.credentials.deleteError'));
    },
  });

  const replaceMut = useMutation({
    mutationFn: ({ id, nextFile }: { id: string; nextFile: File }) =>
      profileDocumentsApi.replaceFile(id, nextFile),
    onSuccess: () => {
      invalidateDocs();
      toast.success(t('profile.credentials.replaceSuccess'));
    },
    onError: () => {
      toast.error(t('profile.credentials.replaceError'));
    },
  });

  const reorderMut = useMutation({
    mutationFn: (documentIds: string[]) => profileDocumentsApi.reorder(documentIds),
    onSuccess: (next) => {
      qc.setQueryData(['profile-documents'], next);
    },
    onError: () => {
      toast.error(t('profile.credentials.reorderError'));
      invalidateDocs();
    },
  });

  const download = useCallback(async (doc: ProfileDocumentDto) => {
    try {
      const { blob, filename } = await profileDocumentsApi.downloadBlob(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || doc.originalName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('profile.credentials.downloadError'));
    }
  }, [t]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = canonicalDocs.map((doc) => doc.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const nextIds = arrayMove(ids, oldIndex, newIndex);
    qc.setQueryData(['profile-documents'], (current: ProfileDocumentDto[] | undefined) => {
      if (!current) return current;
      const byId = new Map(current.map((doc) => [doc.id, doc]));
      return nextIds
        .map((id, index) => {
          const doc = byId.get(id);
          return doc ? { ...doc, sortOrder: index } : null;
        })
        .filter((doc): doc is ProfileDocumentDto => !!doc)
        .concat(current.filter((doc) => !!doc.replacesDocumentId));
    });
    reorderMut.mutate(nextIds);
  };

  const isCaseStudyKind = kind === ProfileDocumentKind.CaseStudyFile;
  const isUploading = uploadMut.isPending || draftMut.isPending;

  const handleUpload = () => {
    if (isCaseStudyKind) {
      draftMut.mutate();
      return;
    }
    uploadMut.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profile.credentials.title')}</CardTitle>
        <CardDescription>{t('profile.credentials.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>{t('profile.credentials.uploadKind')}</Label>
          <Select
            value={kind}
            onValueChange={(v) => setKind(v as ProfileDocumentKindType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ProfileDocumentKindValues.map((k) => (
                <SelectItem key={k} value={k}>
                  {t(`profile.credentials.kind.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InputFile
            file={file}
            onChange={setFile}
            label={t('profile.credentials.file')}
            accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
            disabled={isUploading}
          />
          <p className="text-xs text-muted-foreground">
            {isCaseStudyKind
              ? t('profile.credentials.caseStudyFileHint')
              : t('profile.credentials.fileHint')}
          </p>
          <Button
            type="button"
            disabled={!file || isUploading}
            onClick={handleUpload}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isCaseStudyKind
              ? t('profile.credentials.openCaseStudyForm')
              : t('profile.credentials.upload')}
          </Button>
        </div>

        <div className="space-y-2">
          <Label>{t('profile.credentials.listTitle')}</Label>
          {isLoading && <Loading text={t('generic.loading')} />}
          {!isLoading && canonicalDocs.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('profile.credentials.empty')}
            </p>
          )}
          {canonicalDocs.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={canonicalDocs.map((doc) => doc.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {canonicalDocs.map((doc) => (
                    <SortableDocumentRow
                      key={doc.id}
                      doc={doc}
                      replacement={replacementsByOriginal.get(doc.id)}
                      onDownload={download}
                      onReplace={(nextFile) => replaceMut.mutate({ id: doc.id, nextFile })}
                      onDelete={(id) => deleteMut.mutate(id)}
                      isDeleting={deleteMut.isPending}
                      isReplacing={replaceMut.isPending}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SortableDocumentRow({
  doc,
  replacement,
  onDownload,
  onReplace,
  onDelete,
  isDeleting,
  isReplacing,
}: {
  doc: ProfileDocumentDto;
  replacement?: ProfileDocumentDto;
  onDownload: (doc: ProfileDocumentDto) => void;
  onReplace: (file: File) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  isReplacing: boolean;
}) {
  const { t } = useTranslation();
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: doc.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const display = replacement ?? doc;
  const statusKey = credentialItemStatusKey(display.reviewStatus, !!replacement);
  const liveStatusKey = credentialItemStatusKey(doc.reviewStatus, false);

  const pickReplacement = (next: File | null) => {
    if (!next) return;
    const validation = validateProfileDocumentFile(next);
    if (!validation.ok) {
      toast.error(t(validation.messageKey));
      return;
    }
    onReplace(next);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
    >
      <div className="flex min-w-0 items-start gap-2">
        <button
          type="button"
          className="mt-0.5 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label={t('profile.credentials.reorder')}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <div className="font-medium">{doc.originalName}</div>
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <span>{t(`profile.credentials.kind.${doc.kind}`)}</span>
            <Badge variant={credentialStatusBadgeVariant(liveStatusKey)}>
              {t(`profile.credentials.reviewStatus.${liveStatusKey}`)}
            </Badge>
            {replacement && (
              <Badge variant={credentialStatusBadgeVariant(statusKey)}>
                {t(`profile.credentials.reviewStatus.${statusKey}`)}
              </Badge>
            )}
          </div>
          {replacement && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('profile.credentials.pendingVersion', { name: replacement.originalName })}
            </p>
          )}
          {display.reviewStatus === CredentialReviewStatus.Rejected &&
            display.rejectionReason && (
              <div className="text-destructive text-xs mt-1">
                {display.rejectionReason}
              </div>
            )}
        </div>
      </div>
      <div className="flex gap-1">
        <input
          ref={replaceInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(e) => {
            pickReplacement(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onDownload(doc)}
          aria-label={t('profile.credentials.download')}
        >
          <Download className="h-4 w-4" />
        </Button>
        {replacement && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onDownload(replacement)}
            aria-label={t('profile.credentials.downloadPending')}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isReplacing}
          onClick={() => replaceInputRef.current?.click()}
          aria-label={t('profile.credentials.replace')}
        >
          <Replace className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onDelete(replacement?.id ?? doc.id)}
          disabled={isDeleting}
          aria-label={t('generic.delete')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

function InputFile({
  accept,
  label,
  file,
  onChange,
  disabled = false,
}: {
  accept: string;
  label: string;
  file: File | null;
  onChange: (f: File | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const inputId = 'profile-credentials-file';

  const pickFile = (next: File | null) => {
    if (!next) {
      onChange(null);
      return;
    }
    const validation = validateProfileDocumentFile(next);
    if (!validation.ok) {
      toast.error(t(validation.messageKey));
      return;
    }
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>
      <div
        className={`relative rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/40'
        } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          pickFile(e.dataTransfer.files?.[0] ?? null);
        }}
      >
        <div className="flex flex-col items-center justify-center text-center">
          <Upload className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {t('fileUpload.dropFilesHere')}{' '}
            <label
              htmlFor={inputId}
              className="cursor-pointer text-primary underline-offset-4 hover:underline"
            >
              {t('fileUpload.browse')}
            </label>
          </p>
          <input
            id={inputId}
            type="file"
            accept={accept}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => {
              pickFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
        </div>
      </div>
      {file && (
        <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <div className="min-w-0">
            <p className="truncate font-medium">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            onClick={() => pickFile(null)}
            aria-label={t('generic.remove')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
